import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
  List,
  ListItem,
  Chip,
  Alert,
  Divider,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import { useTheme } from '@/contexts/ThemeContext'
import { useNotification } from '@/hooks/useNotification'
import { getAddress, isAddress } from 'viem'
import useSafe from '@/localsafe.eth/hooks/useSafe'
import {
  encodeContractCall,
  buildSafeTransactionParams,
} from '@/utils/safeTransactionHelpers'
import { submitSafeTransactionToService } from '@/utils/safeTransactionService'
import { isUserRejectedError } from '@/utils/errorHandler'
import { formatAddress } from '@/utils/formatting'
import { useWallet } from '@/hooks/useWallet'
import {
  MODAL_PAPER_STYLES,
  MODAL_TITLE_STYLES,
  MODAL_CONTENT_STYLES,
  MODAL_BUTTON_STYLES,
} from '@/components/shared/modalStyles'
import { type Abi } from 'viem'
import SafeABI from '@/abis/Safe.json'

interface SafeSettingsModalProps {
  open: boolean
  onClose: () => void
  safeAddress: string
  currentOwners: string[]
  currentThreshold: number
  chainId: string
  onSuccess?: () => void
}

export default function SafeSettingsModal({
  open,
  onClose,
  safeAddress,
  currentOwners,
  currentThreshold,
  chainId: chainIdStr,
  onSuccess,
}: SafeSettingsModalProps) {
  const { colors } = useTheme()
  const { showSuccess, showError } = useNotification()
  const { address: userAddress } = useWallet()
  const chainId = parseInt(chainIdStr)
  const safeAddressTyped = safeAddress as `0x${string}`

  const {
    isReady,
    buildSafeTransaction,
    signSafeTransaction,
    executeSafeTransaction,
    getSafeTransactionHash,
    isTransactionReadyToExecute,
  } = useSafe(safeAddressTyped)

  const [newOwnerAddress, setNewOwnerAddress] = useState('')
  const [newOwnerAddressError, setNewOwnerAddressError] = useState<string>('')
  const [ownersToRemove, setOwnersToRemove] = useState<Set<string>>(new Set())
  const [ownersToAdd, setOwnersToAdd] = useState<string[]>([])
  const [newThreshold, setNewThreshold] = useState<string>(
    String(currentThreshold)
  )
  const [isProcessing, setIsProcessing] = useState(false)

  useEffect(() => {
    if (open) {
      setOwnersToRemove(new Set())
      setOwnersToAdd([])
      setNewThreshold(String(currentThreshold))
      setNewOwnerAddress('')
      setNewOwnerAddressError('')
      setIsProcessing(false)
    }
  }, [open, currentThreshold])

  const finalOwnerCount =
    currentOwners.length - ownersToRemove.size + ownersToAdd.length

  const hasChanges =
    ownersToRemove.size > 0 ||
    ownersToAdd.length > 0 ||
    parseInt(newThreshold) !== currentThreshold

  const thresholdNum = parseInt(newThreshold)
  const isThresholdValid =
    !isNaN(thresholdNum) && thresholdNum >= 1 && thresholdNum <= finalOwnerCount

  const validateOwnerAddress = (address: string): string | null => {
    if (!address) {
      return null
    }

    if (!isAddress(address)) {
      return 'Invalid address format'
    }

    const normalizedAddress = getAddress(address).toLowerCase()

    if (
      currentOwners.some(
        (o) => getAddress(o).toLowerCase() === normalizedAddress
      )
    ) {
      return 'This address is already an owner'
    }

    if (
      ownersToAdd.some((o) => getAddress(o).toLowerCase() === normalizedAddress)
    ) {
      return 'This address is already queued to be added'
    }

    if (ownersToRemove.has(normalizedAddress)) {
      return 'This address is queued for removal. Remove it from the removal queue first.'
    }

    return null
  }

  const handleNewOwnerAddressChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value
    setNewOwnerAddress(value)
    const error = validateOwnerAddress(value)
    setNewOwnerAddressError(error || '')
  }

  const handleThresholdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9]/g, '')
    if (value === '') {
      setNewThreshold('')
      return
    }
    const numValue = parseInt(value)
    if (!isNaN(numValue) && numValue >= 1) {
      setNewThreshold(value)
    }
  }

  const handleThresholdKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      e.key === '-' ||
      e.key === 'e' ||
      e.key === 'E' ||
      e.key === '+' ||
      e.key === '.'
    ) {
      e.preventDefault()
    }
  }

  const handleAddOwnerToQueue = () => {
    const error = validateOwnerAddress(newOwnerAddress)
    if (error) {
      showError(error)
      return
    }

    setOwnersToAdd([...ownersToAdd, newOwnerAddress])
    setNewOwnerAddress('')
    setNewOwnerAddressError('')
  }

  const handleToggleRemoveOwner = (owner: string) => {
    const normalizedOwner = getAddress(owner).toLowerCase()
    const newSet = new Set(ownersToRemove)

    if (newSet.has(normalizedOwner)) {
      newSet.delete(normalizedOwner)
    } else {
      newSet.add(normalizedOwner)
    }

    setOwnersToRemove(newSet)
  }

  const handleRemoveFromAddQueue = (index: number) => {
    setOwnersToAdd(ownersToAdd.filter((_, i) => i !== index))
  }

  const handleCreateTransaction = async () => {
    const thresholdNum = parseInt(newThreshold)
    if (
      isNaN(thresholdNum) ||
      thresholdNum < 1 ||
      thresholdNum > finalOwnerCount
    ) {
      showError(
        `Threshold must be between 1 and ${finalOwnerCount} (final owner count)`
      )
      return
    }

    if (!hasChanges) {
      showError('No changes to apply')
      return
    }

    if (!isReady) {
      showError('Safe is not ready')
      return
    }

    setIsProcessing(true)
    try {
      const transactions: Array<{
        to: string
        value: string
        data: string
        operation: number
      }> = []

      let workingOwners = [...currentOwners]
      const ownersToRemoveArray = Array.from(ownersToRemove)
      const ownersToAddArray = [...ownersToAdd]

      const minSwapCount = Math.min(
        ownersToRemoveArray.length,
        ownersToAddArray.length
      )

      for (let i = 0; i < minSwapCount; i++) {
        const ownerToRemove = ownersToRemoveArray[i]
        const ownerToAdd = ownersToAddArray[i]

        const ownerIndex = workingOwners.findIndex(
          (o) =>
            getAddress(o).toLowerCase() ===
            getAddress(ownerToRemove).toLowerCase()
        )

        if (ownerIndex === -1) {
          throw new Error(`Owner ${ownerToRemove} not found`)
        }

        const prevOwner =
          ownerIndex === 0
            ? ('0x0000000000000000000000000000000000000001' as `0x${string}`)
            : (getAddress(workingOwners[ownerIndex - 1]) as `0x${string}`)

        const data = encodeContractCall(SafeABI as Abi, 'swapOwner', [
          prevOwner,
          getAddress(ownerToRemove),
          getAddress(ownerToAdd),
        ])

        transactions.push(
          buildSafeTransactionParams({
            to: safeAddress,
            data,
          })
        )

        workingOwners[ownerIndex] = ownerToAdd
      }

      for (let i = minSwapCount; i < ownersToRemoveArray.length; i++) {
        const ownerToRemove = ownersToRemoveArray[i]

        const ownerIndex = workingOwners.findIndex(
          (o) =>
            getAddress(o).toLowerCase() ===
            getAddress(ownerToRemove).toLowerCase()
        )

        if (ownerIndex === -1) {
          throw new Error(`Owner ${ownerToRemove} not found`)
        }

        const prevOwner =
          ownerIndex === 0
            ? ('0x0000000000000000000000000000000000000001' as `0x${string}`)
            : (getAddress(workingOwners[ownerIndex - 1]) as `0x${string}`)

        const data = encodeContractCall(SafeABI as Abi, 'removeOwner', [
          prevOwner,
          getAddress(ownerToRemove),
          BigInt(thresholdNum),
        ])

        transactions.push(
          buildSafeTransactionParams({
            to: safeAddress,
            data,
          })
        )

        workingOwners = workingOwners.filter((_, i) => i !== ownerIndex)
      }

      for (let i = minSwapCount; i < ownersToAddArray.length; i++) {
        const ownerToAdd = ownersToAddArray[i]

        const data = encodeContractCall(
          SafeABI as Abi,
          'addOwnerWithThreshold',
          [getAddress(ownerToAdd), BigInt(thresholdNum)]
        )

        transactions.push(
          buildSafeTransactionParams({
            to: safeAddress,
            data,
          })
        )

        workingOwners.push(ownerToAdd)
      }

      if (thresholdNum !== currentThreshold) {
        const data = encodeContractCall(SafeABI as Abi, 'changeThreshold', [
          BigInt(thresholdNum),
        ])

        transactions.push(
          buildSafeTransactionParams({
            to: safeAddress,
            data,
          })
        )
      }

      if (transactions.length === 0) {
        showError('No transactions to create')
        setIsProcessing(false)
        return
      }

      const safeTx = await buildSafeTransaction(transactions)

      if (!safeTx) {
        showError('Failed to build Safe transaction')
        setIsProcessing(false)
        return
      }

      let signedTx
      try {
        signedTx = await signSafeTransaction(safeTx)

        if (chainId && userAddress) {
          try {
            const submitResult = await submitSafeTransactionToService(
              signedTx,
              safeAddressTyped,
              chainId,
              userAddress,
              getSafeTransactionHash
            )
            if (!submitResult.success) {
              console.error(
                'Failed to submit transaction to Safe Transaction Service:',
                submitResult.error
              )
            }
          } catch (error) {
            console.error(
              'Error submitting transaction to Safe Transaction Service:',
              error
            )
          }
        }
      } catch (error) {
        setIsProcessing(false)
        if (isUserRejectedError(error)) {
          showError('You cancelled the signature request.')
        } else {
          showError(
            error instanceof Error
              ? error.message
              : 'Failed to sign Safe transaction'
          )
        }
        return
      }

      const isReady = await isTransactionReadyToExecute(signedTx)

      if (isReady) {
        try {
          const hash = await executeSafeTransaction(signedTx)
          if (hash) {
            setIsProcessing(false)
            showSuccess('Settings updated successfully!')
            onSuccess?.()
            onClose()
          }
        } catch (error) {
          setIsProcessing(false)
          if (isUserRejectedError(error)) {
            showError('You cancelled the execution transaction.')
          } else {
            const errorMessage =
              error instanceof Error ? error.message : String(error)
            showError(errorMessage || 'Failed to execute transaction')
          }
        }
      } else {
        setIsProcessing(false)
        getSafeTransactionHash(signedTx)
          .then((txHash) => {
            showSuccess(
              `Transaction created and signed! It requires more signatures before execution. Transaction hash: ${txHash.slice(
                0,
                10
              )}...`
            )
            onSuccess?.()
            onClose()
          })
          .catch(() => {
            showSuccess(
              'Transaction created and signed! It requires more signatures before execution.'
            )
            onSuccess?.()
            onClose()
          })
      }
    } catch (error) {
      setIsProcessing(false)
      console.error('Failed to create batch update:', error)
      showError(
        `Failed to create transaction: ${
          error instanceof Error ? error.message : String(error)
        }`
      )
    }
  }

  const dividerStyles = {
    borderColor: colors.primary,
    opacity: 0.3,
    my: 2,
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          ...MODAL_PAPER_STYLES,
          backgroundColor: colors.background,
        },
      }}
    >
      <DialogTitle
        sx={{
          ...MODAL_TITLE_STYLES,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="h6" component="span">
          Safe Settings
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          disabled={isProcessing}
          sx={{
            color: colors.text,
            '&:hover': {
              backgroundColor: `${colors.primary}22`,
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={MODAL_CONTENT_STYLES}>
        <Alert
          severity="info"
          sx={{
            mb: 3,
            backgroundColor: `${colors.primary}22`,
            color: colors.text,
          }}
        >
          <Typography variant="body2">
            Queue up multiple owner changes and update the threshold. All
            changes will be batched into a single transaction.
          </Typography>
        </Alert>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 2,
            mb: 3,
          }}
        >
          <Box
            sx={{
              p: 2,
              border: `1px solid ${colors.primary}33`,
              borderRadius: 1,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ color: colors.text, mb: 1, fontWeight: 600 }}
            >
              Current State
            </Typography>
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}
            >
              <Typography variant="body2" sx={{ color: colors.text }}>
                Owners:
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: colors.text, fontWeight: 600 }}
              >
                {currentOwners.length}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" sx={{ color: colors.text }}>
                Threshold:
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: colors.text, fontWeight: 600 }}
              >
                {currentThreshold}
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              p: 2,
              border: `1px solid ${colors.primary}33`,
              borderRadius: 1,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ color: colors.text, mb: 1, fontWeight: 600 }}
            >
              Final State
            </Typography>
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}
            >
              <Typography variant="body2" sx={{ color: colors.text }}>
                Owners:
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color:
                    finalOwnerCount !== currentOwners.length
                      ? colors.primary
                      : colors.text,
                  fontWeight: 600,
                }}
              >
                {finalOwnerCount}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" sx={{ color: colors.text }}>
                Threshold:
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color:
                    parseInt(newThreshold) !== currentThreshold
                      ? colors.primary
                      : colors.text,
                  fontWeight: 600,
                }}
              >
                {newThreshold || currentThreshold}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Divider sx={dividerStyles} />

        <Box sx={{ mb: 3 }}>
          <Typography
            variant="subtitle1"
            sx={{ color: colors.text, mb: 2, fontWeight: 600 }}
          >
            Add Owners
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="0x..."
              value={newOwnerAddress}
              onChange={handleNewOwnerAddressChange}
              disabled={isProcessing}
              error={!!newOwnerAddressError}
              helperText={newOwnerAddressError}
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: colors.text,
                  '& fieldset': {
                    borderColor: newOwnerAddressError
                      ? `${colors.error}66`
                      : `${colors.primary}66`,
                  },
                  '&:hover fieldset': {
                    borderColor: newOwnerAddressError
                      ? colors.error
                      : colors.primary,
                  },
                  '&.Mui-error fieldset': {
                    borderColor: `${colors.error}66`,
                  },
                },
                '& .MuiFormHelperText-root': {
                  color: newOwnerAddressError ? colors.error : colors.text,
                  opacity: 0.7,
                },
              }}
            />
            <Button
              variant="outlined"
              onClick={handleAddOwnerToQueue}
              disabled={
                isProcessing || !newOwnerAddress || !!newOwnerAddressError
              }
              startIcon={<AddIcon />}
              sx={{
                color: colors.primary,
                borderColor: colors.primary,
                '&:hover': {
                  borderColor: colors.primary,
                  backgroundColor: `${colors.primary}22`,
                },
              }}
            >
              Add
            </Button>
          </Box>

          {ownersToAdd.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography
                variant="body2"
                sx={{ color: colors.text, mb: 1, fontWeight: 600 }}
              >
                Queued to Add:
              </Typography>
              <List
                sx={{
                  border: `1px solid ${colors.primary}33`,
                  borderRadius: 1,
                  p: 1,
                }}
              >
                {ownersToAdd.map((addr, idx) => (
                  <ListItem
                    key={idx}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      py: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: colors.text,
                        fontFamily: 'monospace',
                        fontSize: '0.85rem',
                      }}
                    >
                      {formatAddress(addr)}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveFromAddQueue(idx)}
                      disabled={isProcessing}
                      sx={{
                        color: colors.error,
                        '&:hover': {
                          backgroundColor: `${colors.error}22`,
                        },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </Box>

        <Divider sx={dividerStyles} />

        <Box sx={{ mb: 3 }}>
          <Typography
            variant="subtitle1"
            sx={{ color: colors.text, mb: 2, fontWeight: 600 }}
          >
            Remove Owners
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: colors.text, opacity: 0.7, mb: 2 }}
          >
            Click owners to toggle removal
          </Typography>
          <List
            sx={{
              maxHeight: '200px',
              overflowY: 'auto',
              border: `1px solid ${colors.primary}33`,
              borderRadius: 1,
              p: 1,
            }}
          >
            {currentOwners.map((owner) => {
              const isMarkedForRemoval = ownersToRemove.has(
                getAddress(owner).toLowerCase()
              )
              return (
                <ListItem
                  key={owner}
                  onClick={() => handleToggleRemoveOwner(owner)}
                  sx={{
                    cursor: 'pointer',
                    py: 1,
                    borderRadius: 1,
                    backgroundColor: isMarkedForRemoval
                      ? `${colors.error}22`
                      : 'transparent',
                    border: isMarkedForRemoval
                      ? `1px solid ${colors.error}66`
                      : 'none',
                    '&:hover': {
                      backgroundColor: isMarkedForRemoval
                        ? `${colors.error}33`
                        : `${colors.primary}22`,
                    },
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: colors.text,
                      fontFamily: 'monospace',
                      fontSize: '0.85rem',
                      flex: 1,
                    }}
                  >
                    {formatAddress(owner)}
                  </Typography>
                  {isMarkedForRemoval && (
                    <Chip
                      label="Removing"
                      size="small"
                      sx={{
                        backgroundColor: colors.error,
                        color: colors.text,
                        fontSize: '0.7rem',
                      }}
                    />
                  )}
                </ListItem>
              )
            })}
          </List>
        </Box>

        <Divider sx={dividerStyles} />

        <Box sx={{ mb: 2 }}>
          <Typography
            variant="subtitle1"
            sx={{ color: colors.text, mb: 2, fontWeight: 600 }}
          >
            New Threshold
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={newThreshold}
            onChange={handleThresholdChange}
            onKeyDown={handleThresholdKeyDown}
            disabled={isProcessing}
            placeholder="Enter threshold"
            helperText={`Must be between 1 and ${finalOwnerCount} (final owner count)`}
            sx={{
              '& .MuiOutlinedInput-root': {
                color: colors.text,
                '& fieldset': {
                  borderColor: `${colors.primary}66`,
                },
                '&:hover fieldset': {
                  borderColor: colors.primary,
                },
              },
              '& .MuiFormHelperText-root': {
                color: colors.text,
                opacity: 0.7,
              },
            }}
          />
        </Box>

        {hasChanges && (
          <Alert
            severity="warning"
            sx={{ mt: 2, backgroundColor: `${colors.primary}22` }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              Changes Summary:
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2 }}>
              {ownersToRemove.size > 0 && (
                <li>
                  <Typography variant="body2">
                    Remove {ownersToRemove.size} owner(s)
                  </Typography>
                </li>
              )}
              {ownersToAdd.length > 0 && (
                <li>
                  <Typography variant="body2">
                    Add {ownersToAdd.length} owner(s)
                  </Typography>
                </li>
              )}
              {parseInt(newThreshold) !== currentThreshold && (
                <li>
                  <Typography variant="body2">
                    Change threshold from {currentThreshold} to{' '}
                    {newThreshold || currentThreshold}
                  </Typography>
                </li>
              )}
            </Box>
          </Alert>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 2,
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          disabled={isProcessing}
          sx={{
            ...MODAL_BUTTON_STYLES,
            color: colors.text,
            borderColor: `${colors.primary}66`,
            '&:hover': {
              borderColor: colors.primary,
              backgroundColor: `${colors.primary}22`,
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleCreateTransaction}
          variant="contained"
          disabled={
            isProcessing || !hasChanges || !isReady || !isThresholdValid
          }
          startIcon={
            isProcessing ? (
              <CircularProgress size={16} sx={{ color: colors.text }} />
            ) : null
          }
          sx={{
            ...MODAL_BUTTON_STYLES,
            backgroundColor: colors.primary,
            color: colors.background,
            '&:hover': {
              backgroundColor: colors.primary,
              opacity: 0.9,
            },
            '&:disabled': {
              backgroundColor: `${colors.primary}66`,
              color: colors.text,
            },
          }}
        >
          {isProcessing ? 'Creating Transaction...' : 'Create Transaction'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
